import React from 'react';
import { ArrowLeft, ArrowRight, Wand2, AlertCircle } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import FormatPicker from '../FormatPicker';
import AskAIFormatHelper from '../AskAIFormatHelper';
import WizardFrame from './WizardFrame';

/**
 * Wizard step 1 of 4: pick a tournament format. Deliberately doesn't
 * auto-advance on card click — a separate "Next" step gives the organizer a
 * chance to compare formats before committing, matching the rest of the
 * wizard's one-decision-per-screen shape.
 *
 * Back only renders in standalone mode (Explore Tournament Formats) — that's
 * the only context where there's an actual previous step (Test Data) to
 * return to; opened from a real tournament's Schedule tab, this is the
 * wizard's first step, so there's nothing behind it.
 *
 * "Next" (see useScheduleBuilder's `proceedFromFormat`) doesn't always land
 * on Parameters — it skips straight to Groups or generation when every input
 * that screen would collect is already available from Test Data / the real
 * tournament's own data, which is everything except legs. `nextLabel` (see
 * `formatStepNextLabel`) previews which of the three actually happens,
 * rather than a generic "Next".
 */
const SelectFormatStep = ({ format, selectFormat, proceedFromFormat, nextLabel, standalone, backToTestData, eligibleFormats, error, participantCount, sportName }) => {
  const { theme } = useTheme();
  // Guards against a format that was already selected (e.g. by Back-ing up
  // after lowering the count, or changing sport) becoming invalid —
  // FormatPicker's own disabled cards stop a NEW invalid pick, this stops
  // proceeding with a stale one. Same map both places (formatEligibility.js).
  const ineligible = format && !eligibleFormats[format.id].enabled;
  return (
    <WizardFrame
      title="Choose a format"
      subtitle="Pick how the tournament plays out — everything after this adapts to your choice."
      footer={
        <>
          {standalone ? (
            <button
              type="button"
              onClick={backToTestData}
              className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${theme.buttonGhost}`}
            >
              <ArrowLeft size={16} />
              Back
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={proceedFromFormat}
            disabled={!format || ineligible}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all ${theme.shadow} ${
              format && !ineligible ? `${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]` : `${theme.buttonGhost} cursor-not-allowed opacity-50`
            }`}
          >
            {nextLabel}
            {nextLabel === 'Generate Schedule' ? <Wand2 size={16} /> : <ArrowRight size={16} />}
          </button>
        </>
      }
    >
      <AskAIFormatHelper participantCount={participantCount} sportName={sportName} onSelectFormat={selectFormat} eligibleFormats={eligibleFormats} />
      <FormatPicker selectedFormatId={format?.id} onSelect={selectFormat} eligibleFormats={eligibleFormats} />
      {error && (
        <div className={`mt-3 flex items-center gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500`}>
          <AlertCircle size={15} />
          {error}
        </div>
      )}
    </WizardFrame>
  );
};

export default SelectFormatStep;
